/** @tsx tsx */
import {keyframes} from "@emotion/react";
import CircularProgress from "@material-ui/core/CircularProgress";
import MaterialTable, {
    TableProps as MaterialTableProps,
} from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import styled from "@emotion/styled";
import {useTokens} from "@nike/nike-design-system-components";
import {HeaderCell} from "./HeaderCell";
import {TableHead} from "@material-ui/core";
import {TableColumn, SortOrders, ColumnTypes} from "../helpers/types";
import {defaultValueGetter} from "../helpers/functions";


const StyledTableBody = styled(TableBody)`
  position: relative;
`;

const fadeIn = keyframes`
  0% { opacity: 0.1 }
  100% { opacity: 1 }
`;

export interface BaseOrderProps {
    order: SortOrders;
    orderBy: string;
    onSort: (property: ColumnTypes) => void;
}

export type BaseProps = Omit<MaterialTableProps, "onChange"> & BaseOrderProps;

export interface TableProps<T> extends BaseProps {
    columns: TableColumn<T>[];
    loading?: boolean;
    rows?: T[];
    selectedRow?: object;
    setSelectedRow?: (value: unknown) => void;
    emptyMessage?: string;
}

const Table = <T extends Record<string, unknown> = Record<string, unknown>>({
    columns, loading, onSort, order, orderBy, rows = [],
    selectedRow, setSelectedRow, emptyMessage, ...props
}: TableProps<T>) => {
    const tokens = useTokens();

    const StyledTableContainer = styled(TableContainer)`
    border-radius: 8px !important;
  `;

    const LoadingContainer = styled(TableCell)<{ rows?: number }>`
    ${({rows}) => `
    position: ${rows ? "absolute" : "relative"};
  `}
    animation: ${fadeIn} 0.2s;
    vertical-align: center;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    text-align: center !important;
  `;

    const EmptyContainer = styled(TableCell)`
    padding: 27px;
    text-align: center;
    color: ${tokens.podiumCdsColorTextSecondary};
  `;

    const StyledMaterialTable = styled(MaterialTable)`
    border-collapse: separate !important;
    border: 1.5px solid #e5e5e5;
    border-radius: 8px;
    & td,
    & th {
      color: ${() => tokens.podiumCdsColorTextPrimary};
      font: ${() => tokens.podiumCdsTypographyBody1};
    }
    & thead tr th:first-of-type {
      border-radius: 8px 0 0 0;
    }
    & thead tr th:last-child {
      border-radius: 0 8px 0 0;
    }
    & tbody tr:last-child td {
      border-bottom: none;
    }
    & .Mui-selected {
      background-color: unset !important;
    }
    & .Mui-hover {
      backgroundColor: primary.light
    }
  }}
  `;

  return (
    <StyledTableContainer>
      <StyledMaterialTable {...props}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <HeaderCell
                key={`header-${column.id}`}
                className={column.id}
                order={order}
                orderBy={orderBy}
                onSort={onSort}
                sortable={column.sortable}
                property={column.id}
                width={column.width}
              >
                {column.header}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <StyledTableBody>
          {loading && (
            <TableRow>
              <LoadingContainer colSpan={columns.length} rows={rows?.length}>
                <CircularProgress color="inherit" />
              </LoadingContainer>
            </TableRow>
          )}
          {rows.length
            ? rows.map((row, index) => (
                <TableRow hover key={(row.id as string) ?? index} onClick={() => {
                    setSelectedRow(row)
                }}>
                  {columns.map((column) => (
                    <TableCell width={column.width} key={column.id}>
                      {
                        (column.valueGetter || defaultValueGetter)(
                          row,
                          column.id,
                          index,
                          rows
                        ) as string
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : !loading && (
                <TableRow>
                  <EmptyContainer colSpan={columns.length}>
                      {emptyMessage ? emptyMessage :  'Uh-oh! No records were found.'}
                  </EmptyContainer>
                </TableRow>
              )}
        </StyledTableBody>
      </StyledMaterialTable>
    </StyledTableContainer>
  );
};


export default Table;
